import React, { useEffect, useState } from 'react';
import Stack from 'react-bootstrap/Stack';
import LoadingSpinner from '../common/loading-spinner';
import { useAuth } from '../../providers/auth-provider';
import { CUTOFF_DNR, CUTOFF_HR, CUTOFF_RWC, getNineBoxValue, getRecommendationCutoff } from './candidate-scoring-util';
import ClientDashboardCandidateCharts from './client-dashboard-candidate-charts';
import ClientDashboardLeadershipCharts from './client-dashboard-leadership-charts';
import { getPercentileAttributes, getUserAggregation } from '../../api/dashboards-api';
import { buildParticipantSummaries, getUserParticipationStatuses } from '../../api/participation';
import {
  areFiltersInUse,
  filtersExist,
  getFilterKeys,
  getOpeningType,
  getStoredFilter,
  LEADERSHIP_OPENING,
} from '../shared/filters/filter-common';
import { DEFAULT_METADATA_PATH, getMetadataValue } from '../../api/metadata';
import { DashboardFiltersContainer } from './dashboard-filters-container';

function ClientDashboardContent() {
  const auth = useAuth();
  const [initialized, setInitialized] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState(undefined);
  const [aggregations, setAggregations] = useState({});
  const [assessmentsSummary, setAssessmentsSummary] = useState(undefined);
  const [ratersAssessmentsSummary, setRatersAssessmentsSummary] = useState(undefined);

  let openingType = getOpeningType();

  const filterCandidates = (candidates) => {
    if (candidates && candidates.length === 0) {
      return [];
    }

    const filterKeys = getFilterKeys();
    if (!filterKeys) {
      setFilteredCandidates([...candidates]);
    }

    const filtered = [];
    const percentileAttribute = getPercentileAttributes().performance;
    candidates.forEach((candidate) => {
      if (candidate.percentiles && candidate.percentiles[percentileAttribute] !== undefined) {
        if (candidate.metadata) {
          let include = true;
          for (const filterKey of filterKeys) {
            const value = getMetadataValue(candidate.metadata, filterKey, DEFAULT_METADATA_PATH, '').toLowerCase();
            if (value.length > 0) {
              const filter = getStoredFilter(filterKey);
              for (const item of filter.items) {
                if (!item.selected && item.lcValue === value) {
                  include = false;
                  break;
                }
              }
            } else {
              include = false;
            }
            if (!include) {
              break;
            }
          }
          if (include) {
            filtered.push(candidate);
          }
        } else {
          filtered.push(candidate);
        }
      }
    });
    return filtered;
  };

  const buildAggregations = (serverCandidates) => {
    const percentileAttributes = getPercentileAttributes();
    const totalCandidates = serverCandidates.length;
    const aggs = {
      totalCandidates,
      doNotRecommend: {
        count: 0,
        percent: 0,
      },
      recommendWithCaution: {
        count: 0,
        percent: 0,
      },
      recommend: {
        count: 0,
        percent: 0,
      },
      highlyRecommend: {
        count: 0,
        percent: 0,
      },
      highPerform: {
        count: 0,
        percent: 0,
      },
      highLoyalty: {
        count: 0,
        percent: 0,
      },
      nineBoxValues: {
        counts: [0, 0, 0, 0, 0, 0, 0, 0, 0],
        candidates: [[], [], [], [], [], [], [], [], []],
        percentages: [0, 0, 0, 0, 0, 0, 0, 0, 0],
      },
    };

    if (aggs.totalCandidates > 0) {
      serverCandidates.forEach((candidate) => {
        const cutoff = getRecommendationCutoff(candidate);
        switch (cutoff) {
          case CUTOFF_HR:
            aggs.highlyRecommend.count += 1;
            break;
          case CUTOFF_RWC:
            aggs.recommendWithCaution.count += 1;
            break;
          case CUTOFF_DNR:
            aggs.doNotRecommend.count += 1;
            break;
          default:
            aggs.recommend.count += 1;
        }

        const nineBoxValue = getNineBoxValue(candidate);
        aggs.nineBoxValues.counts[nineBoxValue] += 1;
        aggs.nineBoxValues.candidates[nineBoxValue].push(candidate);

        if (candidate.percentiles[percentileAttributes.performance] >= 66) {
          aggs.highPerform.count += 1;
        }
        if (candidate.percentiles[percentileAttributes.loyalty] >= 66) {
          aggs.highLoyalty.count += 1;
        }
      });

      aggs.doNotRecommend.percent = Math.trunc((aggs.doNotRecommend.count / aggs.totalCandidates) * 100);
      aggs.recommendWithCaution.percent = Math.trunc((aggs.recommendWithCaution.count / aggs.totalCandidates) * 100);
      aggs.recommend.percent = Math.trunc((aggs.recommend.count / aggs.totalCandidates) * 100);
      aggs.highlyRecommend.percent = Math.trunc((aggs.highlyRecommend.count / aggs.totalCandidates) * 100);
      aggs.highPerform.percent = Math.trunc((aggs.highPerform.count / aggs.totalCandidates) * 100);
      aggs.highLoyalty.percent = Math.trunc((aggs.highLoyalty.count / aggs.totalCandidates) * 100);

      for (let n = 0; n < 9; n++) {
        if (aggs.nineBoxValues.counts[n] > 0) {
          aggs.nineBoxValues.percentages[n] = Math.trunc((aggs.nineBoxValues.counts[n] / aggs.totalCandidates) * 100);
        } else {
          aggs.nineBoxValues.percentages[n] = 0;
        }
      }
    }

    setAggregations(aggs);
  };

  const filtersUpdated = () => {
    setFilteredCandidates(filterCandidates(candidates));
  };

  const staffingUpdated = () => {
    const newOpeningType = getOpeningType();
    if (openingType === LEADERSHIP_OPENING || newOpeningType === LEADERSHIP_OPENING) {
      setFilteredCandidates(filterCandidates(candidates));
    } else {
      buildAggregations(filteredCandidates);
    }

    openingType = newOpeningType;
  };

  useEffect(() => {
    if (filteredCandidates) {
      buildAggregations(filteredCandidates);
    }
  }, [filteredCandidates]);

  useEffect(() => {
    if (candidates?.length > 0) {
      if (filtersExist()) {
        setFilteredCandidates(filterCandidates(candidates));
      } else {
        setFilteredCandidates([...candidates]);
      }
    }
  }, [candidates]);

  useEffect(() => {
    if (!initialized) {
      getUserAggregation(auth).then((aggregation) => {
        setInitialized(true);
        setCandidates(aggregation);
      });
    }
  }, []);

  useEffect(() => {
    getUserParticipationStatuses(auth, auth.isDevelop()).then((participants) => {
      const filtered = areFiltersInUse();
      if (filtered) {
        const summaries = buildParticipantSummaries(
          participants.filter((participant) => {
            for (const candidate of filteredCandidates) {
              if (participant.userId === candidate.userId) {
                return true;
              }
            }
          })
        );
        setAssessmentsSummary(summaries.userSummary);
        setRatersAssessmentsSummary(summaries.ratersSummary);
      } else {
        const summaries = buildParticipantSummaries(participants);
        setAssessmentsSummary(summaries.userSummary);
        setRatersAssessmentsSummary(summaries.ratersSummary);
      }
    });
  }, [filteredCandidates]);

  return (
    <>
      {(!initialized || !assessmentsSummary) && <LoadingSpinner />}
      <Stack className="flex w-100 mx-auto bg-gray-200 pb-4" style={{ height: '85vh' }}>
        <Stack className="dashboard-container flex-column mx-auto pt-4 w-100 overflow-auto">
          {candidates?.length > 0 && (
            <DashboardFiltersContainer
              candidates={filteredCandidates}
              filtersUpdatedCallback={filtersUpdated}
              staffingUpdatedCallback={staffingUpdated}
            />
          )}
          {auth.isSelect() && (
            <>
              <ClientDashboardCandidateCharts
                aggregations={aggregations}
                assessmentsSummary={assessmentsSummary}
                candidates={filteredCandidates}
              />
            </>
          )}
          {auth.isDevelop() && (
            <ClientDashboardLeadershipCharts
              aggregations={aggregations}
              assessmentsSummary={assessmentsSummary}
              ratersAssessmentsSummary={ratersAssessmentsSummary}
              candidates={filteredCandidates}
            />
          )}
        </Stack>
      </Stack>
    </>
  );
}

export default ClientDashboardContent;
